import { useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useIsomorphicLayoutEffect } from 'react-use';

import Braze from '~/src/common/services/Braze';
import { DataDog } from '~/src/common/services/Datadog';
import { FLAGS } from '~/src/common/services/FeatureFlag/utils';
import { logger } from '~/src/common/services/Logger';
import { TagManager } from '~/src/common/services/TagManager';
import Tracker from '~/src/common/services/Tracker';
import { User } from '~/src/queries/types/user';
import { addCountryCode } from '~/src/screens/AccountManagementModal/utils';
import { QUERY_KEYS } from '~/src/screens/App/queries/queries';

export const useAnalyticsInitializer = () => {
  // Gestion des push permissions pour Braze
  useEffect(() => {
    void Braze.requestPushPermission();
  }, []);

  // Tracking de l'utilisateur dans les différents outils d'analytics
  const user = useQueryClient().getQueryData<User>(QUERY_KEYS.user);
  useIsomorphicLayoutEffect(() => {
    // Si l'utilisateur est connu, on envoie les infos
    if (user?.id != null) {
      void Braze.changeUser(user.id);
      Tracker.setUserId(user.id);
      TagManager.setDataLayer({
        email: user.email.trim().toLowerCase(),
        userId: user.id,
        phone: addCountryCode(user.phone),
        new_customer: user.newCustomer,
      });
      logger.setUser?.({ id: user.id, email: user.email });
      // On set le user sur Datadof egalement
      DataDog.setUser(user).catch(err => {
        console.error('Error setting user in DataDog:', err);
      });
    }
    // Sinon on met tout à zéro, sauf Braze qui ne doit pas être reset
    else {
      Tracker.setUserId(undefined);
      TagManager.setDataLayer({ new_customer: true });
      TagManager.setDataLayer({ email: '', userId: '', phone: '' });
      logger.setUser?.(null);
    }
  }, [user]);

  // Mise à jour des feature flags dans amplitude
  // Seuls les flags utilisés dans le code sont envoyés
  const featureFlags = useFlags();
  const [experimentsSetup, setExperimentsSetup] = useState(false);
  useIsomorphicLayoutEffect(() => {
    const experiments = Object.entries(featureFlags)
      .filter(([key, val]) => key in FLAGS && val != null)
      .map(([key, val]) => `${key}=${JSON.stringify(val)}`);
    Tracker.setUserProperties({ experiments });
    setExperimentsSetup(true);
  }, [featureFlags]);

  return { experimentsSetup };
};
